// src/components/BotPlaygroundPage.js — تجربة البوت (ديمو حيّ داخل اللوحة)
import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
  Box, Card, Typography, TextField, IconButton, Button, CircularProgress, Chip,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';

const T = { ink: '#0f172a', muted: '#64748b', faint: '#94a3b8', border: '#e9eef5', brand: '#0ea5e9', brandSoft: '#e6f6fe' };

const Bubble = ({ from, text }) => (
  <Box sx={{ display: 'flex', justifyContent: from === 'user' ? 'flex-start' : 'flex-end', mb: 1.25 }}>
    <Box sx={{
      maxWidth: '75%', px: 2, py: 1.25, fontSize: '0.92rem', whiteSpace: 'pre-wrap', lineHeight: 1.7,
      bgcolor: from === 'user' ? '#ffffff' : T.brandSoft,
      color: T.ink,
      border: `1px solid ${from === 'user' ? T.border : '#bae6fd'}`,
      borderRadius: from === 'user' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
    }}>
      {text}
    </Box>
  </Box>
);

export default function BotPlaygroundPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { from: 'user', text }]);
    setSending(true);
    try {
      const res = await axiosInstance.post('/chatbot/demo-chat/', { message: text });
      setMessages((m) => [...m, { from: 'bot', text: res.data.reply || '(بدون رد)' }]);
    } catch {
      setMessages((m) => [...m, { from: 'bot', text: '⚠️ تعذّر الاتصال — حاول مرة أخرى.' }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    try { await axiosInstance.post('/chatbot/demo-chat/', { reset: true }); } catch { /* no-op */ }
    setMessages([]);
  };

  return (
    <Box>
      <PageHeader
        title="تجربة البوت"
        subtitle="محادثة حيّة مع مساعدك الذكي — نفس المحرك الذي يرد على عملائك في واتساب"
        actions={(
          <Button startIcon={<RestartAltRoundedIcon />} onClick={reset} variant="outlined" size="small"
            sx={{ '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 } }}>
            محادثة جديدة
          </Button>
        )}
      />

      <Card elevation={0} sx={{ maxWidth: 780, mx: 'auto' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: T.brandSoft, display: 'grid', placeItems: 'center' }}>
            <SmartToyRoundedIcon sx={{ color: T.brand, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: T.ink }}>المساعد الذكي</Typography>
            <Chip label="وضع تجريبي — لا يصل شيء لعملائك أو موظفيك" size="small"
              sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fef3d9', color: '#b45309', fontWeight: 700 }} />
          </Box>
        </Box>

        <Box sx={{ height: 440, overflowY: 'auto', p: 2.5, bgcolor: '#f8fafc' }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', pt: 10 }}>
              <Typography sx={{ color: T.muted, fontWeight: 700 }}>ابدأ المحادثة 👋</Typography>
              <Typography sx={{ color: T.faint, fontSize: '0.8rem', mt: 0.5 }}>
                جرّب: «ما هي خدماتكم؟» أو «عايز أحجز موعد بكرة الساعة 5»
              </Typography>
            </Box>
          )}
          {messages.map((m, i) => <Bubble key={i} from={m.from} text={m.text} />)}
          {sending && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <CircularProgress size={18} sx={{ color: T.brand }} />
            </Box>
          )}
          <div ref={endRef} />
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 1 }}>
          <TextField fullWidth size="small" placeholder="اكتب رسالتك كأنك العميل…"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <IconButton onClick={send} disabled={sending || !input.trim()}
            sx={{ bgcolor: T.brand, color: '#fff', '&:hover': { bgcolor: '#0284c7' }, '&.Mui-disabled': { bgcolor: '#e2e8f0' } }}>
            <SendRoundedIcon sx={{ transform: 'rotate(180deg)', fontSize: 20 }} />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
}
